// src/dashboard/ClientPersonality.js
import { useState } from 'react';
import { BarChart, RefreshCw, User, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

const ClientPersonality = ({ profile, refreshProfile }) => {
  const [generatingProfile, setGeneratingProfile] = useState(false);

  // Function to generate personality profile
  const generatePersonalityProfile = async () => {
    if (!profile || !profile.userId) return;
    
    try {
      setGeneratingProfile(true);
      const response = await fetch(`/api/generate-client-profile/${profile.userId}`);
      
      if (!response.ok) {
        throw new Error('Failed to generate personality profile');
      }
      
      // Refresh profile to get the new data
      await refreshProfile();
      
    } catch (error) {
      console.error('Error generating personality profile:', error);
      alert('Failed to generate your personality profile. Please try again later.');
    } finally {
      setGeneratingProfile(false);
    }
  };

  if (!profile) {
    return (
      <div className="text-center py-8">
        <div className="rounded-full bg-yellow-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <BarChart className="text-yellow-600" size={32} />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Profile Found</h3>
        <p className="text-gray-600 mb-6">
          You haven't completed the assessment questionnaire yet.
        </p>
        <Link
          to="/"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-block"
        >
          Take the Assessment
        </Link>
      </div>
    );
  }

  return (
    <div>
      {profile.personalityProfile ? (
        <div>
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-semibold text-blue-800">Your Personality Profile</h2>
            <button
              onClick={generatePersonalityProfile}
              disabled={generatingProfile}
              className="flex items-center text-xs px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
            >
              {generatingProfile ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-blue-700 mr-1"></div>
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw size={12} className="mr-1" /> Refresh
                </>
              )}
            </button>
          </div>
          
          <div className="prose prose-blue max-w-none">
            {profile.personalityProfile.split('\n\n').map((paragraph, i) => (
              <p key={i} className="mb-4 text-gray-700">{paragraph}</p>
            ))}
          </div>
          
          <div className="mt-6 text-sm text-gray-500 italic">
            This personality profile is generated based on your assessment responses.
          </div>
          
          {/* Analysis Traits */}
          {profile.analysisResults && profile.analysisResults.tags && (
            <div className="mt-8 bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Your Personality Traits</h3>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {profile.analysisResults.tags.map((tag, index) => (
                  <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
              
              {profile.analysisResults.summary && (
                <div className="mt-4">
                  <h4 className="text-md font-medium text-gray-700 mb-2">Analysis Summary</h4>
                  <p className="text-gray-700">{profile.analysisResults.summary}</p>
                </div>
              )}
              
              {profile.analysisResults.matchScore && (
                <div className="mt-4">
                  <h4 className="text-md font-medium text-gray-700 mb-2">Match Quality</h4>
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full" 
                        style={{ width: `${profile.analysisResults.matchScore}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{profile.analysisResults.matchScore}%</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Navigation links at the bottom */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex flex-wrap justify-between gap-3">
            <Link
              to="/client-profile"
              className="text-blue-600 hover:underline flex items-center"
            >
              <User size={16} className="mr-1" />
              <span>Back to profile</span>
            </Link>
            
            <Link
              to="/client-profile/responses"
              className="text-blue-600 hover:underline flex items-center"
            >
              <MessageSquare size={16} className="mr-1" />
              <span>View your responses</span>
            </Link>
            
            <Link 
            to="/client-assessment" 
            className="text-blue-600 hover:underline flex items-center"
            >
            <span>Continue assessment</span>
            <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 ml-1" 
                viewBox="0 0 20 20" 
                fill="currentColor"
            >
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            </Link>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="rounded-full bg-blue-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <BarChart className="text-blue-600" size={32} />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Generate Your Personality Profile</h3>
          <p className="text-gray-600 mb-6">
            {profile.responses && profile.responses.length > 0 
              ? "You've completed the assessment! Generate your personality profile to learn more about yourself."
              : "Complete the assessment questionnaire to generate your personality profile."
            }
          </p>
          
          {profile.responses && profile.responses.length > 0 ? (
            <button
              onClick={generatePersonalityProfile}
              disabled={generatingProfile}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
            >
              {generatingProfile ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                  Generating Profile...
                </>
              ) : (
                'Generate Personality Profile'
              )}
            </button>
          ) : (
            <Link
              to="/"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 inline-block"
            >
              Take the Assessment
            </Link>
          )}
          
          {/* Even in the empty state, show the back link */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-center">
            <Link
              to="/client-profile"
              className="text-blue-600 hover:underline flex items-center"
            >
              <User size={16} className="mr-1" />
              <span>Back to profile</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPersonality;